import { useEffect, useCallback } from 'react';
import { safeStorage } from '@/lib/utils/storage';

// Session timeout configuration
const SESSION_TIMEOUT = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
const ACTIVITY_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours of inactivity

export function useSessionManager() {
  const updateLastActivity = useCallback(() => {
    safeStorage.setItem('lastActivity', Date.now().toString());
  }, []);

  const initializeSession = useCallback(() => {
    const now = Date.now().toString();
    safeStorage.setItem('sessionStart', now);
    safeStorage.setItem('lastActivity', now);
  }, []);

  const clearSession = useCallback(() => {
    safeStorage.removeItem('sessionStart');
    safeStorage.removeItem('lastActivity');
  }, []);

  const checkSessionValidity = useCallback(() => {
    const lastActivity = safeStorage.getItem('lastActivity');
    const sessionStart = safeStorage.getItem('sessionStart');

    if (!lastActivity || !sessionStart) {
      return false;
    }

    const now = Date.now();
    const timeSinceActivity = now - parseInt(lastActivity);
    const timeSinceSession = now - parseInt(sessionStart);

    // Auto-logout if session is too old or user has been inactive too long
    if (timeSinceSession > SESSION_TIMEOUT || timeSinceActivity > ACTIVITY_TIMEOUT) {
      return false;
    }

    return true;
  }, []);

  useEffect(() => {
    // Activity listener to update last activity time
    const handleActivity = () => {
      // Only track activity for authenticated sessions. We avoid importing Firebase here
      // so public routes don't pull Firebase/AppCheck into the entry bundle.
      if (safeStorage.getItem('sessionStart')) {
        updateLastActivity();
      }
    };

    // Add activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      // Cleanup event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [updateLastActivity]);

  return {
    initializeSession,
    clearSession,
    checkSessionValidity,
    updateLastActivity,
  };
}

